import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Phone as PhoneIcon, ArrowBack as ArrowBackIcon, Refresh as RefreshIcon } from '@mui/icons-material';

const OtpVerificationScreen = ({
  phoneNumber,
  onVerify,
  onResend,
  onBack,
  isLoading = false,
  error = null,
}) => {
  const theme = useTheme();
  const [otp, setOtp] = useState(['', '', '', '']);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  useEffect(() => {
    // Focus first input on mount
    inputRefs[0].current?.focus();
  }, []);

  useEffect(() => {
    // Timer countdown
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
    setCanResend(true);
    return undefined;
  }, [timer]);

  const handleChange = (index, value) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        inputRefs[index - 1].current?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs[index - 1].current?.focus();
    } else if (e.key === 'ArrowRight' && index < 3) {
      inputRefs[index + 1].current?.focus();
    } else if (e.key === 'Enter' && otp.every((digit) => digit !== '')) {
      handleVerify();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 4);
    if (!/^\d{4}$/.test(pastedData)) return;

    const newOtp = pastedData.split('');
    setOtp(newOtp);
    inputRefs[3].current?.focus();
  };

  const handleVerify = () => {
    const otpCode = otp.join('');
    if (otpCode.length === 4) {
      onVerify(otpCode);
    }
  };

  const handleResend = () => {
    setTimer(60);
    setCanResend(false);
    setOtp(['', '', '', '']);
    inputRefs[0].current?.focus();
    onResend();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isOtpComplete = otp.every((digit) => digit !== '');
  const maskedPhone = phoneNumber ? `***-***-${phoneNumber.slice(-4)}` : '****';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <Paper
        elevation={24}
        sx={{
          p: { xs: 3, sm: 5 },
          maxWidth: 500,
          width: '100%',
          borderRadius: 4,
          boxShadow: (theme) =>
            theme.palette.mode === 'dark'
              ? '0 20px 60px rgba(0, 0, 0, 0.6)'
              : '0 20px 60px rgba(0, 0, 0, 0.3)',
          background: (theme) => theme.palette.background.paper,
        }}
      >
        {/* Back Button */}
        {onBack && (
          <IconButton
            onClick={onBack}
            sx={{
              mb: 2,
              color: 'text.secondary',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        )}

        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
              boxShadow: (theme) => `0 8px 24px ${theme.palette.primary.main}40`,
            }}
          >
            <PhoneIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              mb: 1,
            }}
          >
            Verify Your Identity
          </Typography>

          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
            We've sent a 4-digit code to
          </Typography>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <PhoneIcon fontSize="small" />
            {maskedPhone}
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* OTP Input Fields */}
        <Box
          sx={{
            display: 'flex',
            gap: { xs: 1.5, sm: 2 },
            justifyContent: 'center',
            mb: 4,
          }}
        >
          {otp.map((digit, index) => (
            <TextField
              key={index}
              inputRef={inputRefs[index]}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              disabled={isLoading}
              inputProps={{
                maxLength: 1,
                style: {
                  textAlign: 'center',
                  fontSize: '32px',
                  fontWeight: 700,
                  padding: '16px 0',
                },
              }}
              sx={{
                width: { xs: 60, sm: 80 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.50',
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                  '&.Mui-disabled': {
                    bgcolor: (theme) =>
                      theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'grey.100',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  color: digit ? 'primary.main' : 'text.secondary',
                },
              }}
            />
          ))}
        </Box>

        {/* Verify Button */}
        <LoadingButton
          fullWidth
          size="large"
          variant="contained"
          color="primary"
          onClick={handleVerify}
          loading={isLoading}
          disabled={!isOtpComplete || isLoading}
          sx={{
            mb: 3,
            borderRadius: 2,
            height: 56,
            fontSize: '1.1rem',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: (theme) =>
              isOtpComplete && !isLoading
                ? `0 8px 16px ${theme.palette.primary.main}40`
                : 'none',
            '&:hover': {
              boxShadow: (theme) =>
                isOtpComplete && !isLoading
                  ? `0 12px 20px ${theme.palette.primary.main}50`
                  : 'none',
            },
          }}
        >
          {isLoading ? 'Verifying...' : 'Verify Code'}
        </LoadingButton>

        {/* Resend Section */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {canResend ? "Didn't receive the code?" : `Resend code in ${formatTime(timer)}`}
          </Typography>

          {canResend ? (
            <Button
              variant="text"
              color="primary"
              onClick={handleResend}
              startIcon={<RefreshIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
              }}
            >
              Resend Code
            </Button>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              <CircularProgress size={16} thickness={6} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Wait {formatTime(timer)} to resend
              </Typography>
            </Box>
          )}
        </Box>

        {/* Help Text */}
        <Box
          sx={{
            mt: 4,
            pt: 3,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            textAlign: 'center',
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            This is an extra security step to protect your account.
            <br />
            If you didn't request this code, please contact support.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default OtpVerificationScreen;
